// src/components/admin/AdminSessions.jsx
// User Sessions & Activity — Phase 3b (mobile-responsive)

import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../utils/api';

// ── Helpers ───────────────────────────────────────────────────
const fmtTime  = d => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const initials = n => n?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??';
const AVATAR_COLORS = ['var(--gold)', 'var(--blue)', 'var(--green)', '#f472b6', '#a78bfa'];

function parseUA(ua = '') {
  const s = ua.toLowerCase();
  const device  = /ipad|tablet/i.test(s)       ? '📱 Tablet'
    : /mobile|android|iphone/i.test(s)          ? '📱 Mobile'
    : /windows|macintosh|linux/i.test(s)         ? '🖥 Desktop'
    : '❓ Unknown';
  const os      = /iphone|ipad/i.test(s)        ? 'iOS'
    : /android/i.test(s)                         ? 'Android'
    : /windows/i.test(s)                         ? 'Windows'
    : /macintosh/i.test(s)                       ? 'macOS'
    : /linux/i.test(s)                           ? 'Linux'
    : 'Unknown';
  const browser = /edg\//i.test(s)              ? 'Edge'
    : /chrome/i.test(s)                          ? 'Chrome'
    : /firefox/i.test(s)                         ? 'Firefox'
    : /safari/i.test(s)                          ? 'Safari'
    : 'Unknown';
  return { device, os, browser };
}

function Spinner() {
  return <div style={{ padding: 60, textAlign: 'center', color: 'var(--gray)', fontSize: 13 }}>Loading…</div>;
}

function Tag({ text, color, bg }) {
  return (
    <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, letterSpacing: '.04em', background: bg, color }}>
      {text}
    </span>
  );
}

// ── Responsive styles ─────────────────────────────────────────
const MOBILE_CSS = `
@media (max-width: 640px) {
  .sess-stats-grid { grid-template-columns: repeat(3, 1fr) !important; }
  .sess-table-wrap table { min-width: 600px; }

  /* Active sessions card view on mobile */
  .sess-card { display: flex !important; }
  .sess-table-only { display: none !important; }

  /* Suspicious flags: stack actions */
  .sess-flag-actions { flex-direction: column !important; align-items: stretch !important; }
  .sess-flag-actions button { text-align: center; }
}
@media (min-width: 641px) {
  .sess-card-only { display: none !important; }
}
`;

function InjectStyle({ id, css }) {
  useEffect(() => {
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = css;
    document.head.appendChild(el);
  }, []);
  return null;
}

// ── Confirm Dialog ────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,.75)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--dark2)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 'var(--radius)', padding: '24px 22px', maxWidth: 380, width: '100%' }}>
        <div style={{ fontSize: 14, color: 'var(--off-white)', lineHeight: 1.7, marginBottom: 20 }}>{message}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onConfirm} style={{ flex: 1, padding: '10px 0', background: 'rgba(217,79,79,.15)', border: '1px solid rgba(217,79,79,.4)', color: 'var(--red)', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 'var(--radius)' }}>Confirm</button>
          <button onClick={onCancel}  style={{ flex: 1, padding: '10px 0', background: 'transparent', border: '1px solid rgba(255,255,255,.1)', color: 'var(--gray)', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 'var(--radius)' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── User Session History Modal ────────────────────────────────
function SessionHistoryModal({ userId, userName, onClose }) {
  const [sessions, setSessions] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [confirm,  setConfirm]  = useState(null);
  const [revoking, setRevoking] = useState(null);

  useEffect(() => {
    adminApi.getUserSessions(userId)
      .then(d => setSessions(d.sessions || []))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleRevokeOne = (tokenId) => {
    setConfirm({
      message: 'Revoke this session? The device will be logged out.',
      onConfirm: async () => {
        setConfirm(null);
        setRevoking(tokenId);
        try {
          await adminApi.revokeSession(tokenId);
          setSessions(s => s.filter(t => t.id !== tokenId));
        } finally { setRevoking(null); }
      },
    });
  };

  const handleRevokeAll = () => {
    setConfirm({
      message: `Force-logout ${userName}? All their sessions will be revoked immediately.`,
      onConfirm: async () => {
        setConfirm(null);
        setRevoking('all');
        try {
          await adminApi.revokeUserSessions(userId);
          setSessions([]);
        } finally { setRevoking(null); }
      },
    });
  };

  const now = new Date();

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,.75)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--dark2)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 'var(--radius)', width: '100%', maxWidth: 580, maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ height: 1, background: 'linear-gradient(90deg, var(--gold), transparent 60%)', flexShrink: 0 }}/>

        {/* Header */}
        <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)' }}>Session History</div>
            <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {sessions.length > 0 && (
              <button onClick={handleRevokeAll} disabled={revoking === 'all'} style={{ padding: '6px 10px', background: 'rgba(217,79,79,.1)', border: '1px solid rgba(217,79,79,.3)', color: 'var(--red)', fontSize: 10, fontWeight: 700, letterSpacing: '.05em', cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 4, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                ⊘ Logout All
              </button>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '12px 16px' }}>
          {loading ? <Spinner/> : !sessions.length ? (
            <div style={{ color: 'var(--gray)', fontSize: 13, padding: '20px 0' }}>No sessions found.</div>
          ) : sessions.map(s => {
            const ua     = parseUA(s.userAgent);
            const active = new Date(s.expiresAt) > now;
            const loc    = s.city && s.country ? `${s.city}, ${s.country}` : s.country || null;
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                <div style={{ fontSize: 20, flexShrink: 0 }}>{ua.device.split(' ')[0]}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--off-white)' }}>{ua.device.split(' ').slice(1).join(' ')}</span>
                    <span style={{ fontSize: 11, color: 'var(--gray)' }}>· {ua.os} · {ua.browser}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>
                    {loc && <span>{loc} · </span>}
                    <span style={{ fontFamily: 'monospace', fontSize: 10 }}>{s.ipAddress || '—'}</span>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 2 }}>{fmtTime(s.createdAt)}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                  <Tag text={active ? 'ACTIVE' : 'EXPIRED'} color={active ? 'var(--green)' : 'var(--gray)'} bg={active ? 'rgba(46,204,138,.1)' : 'rgba(148,163,184,.08)'}/>
                  {active && (
                    <button onClick={() => handleRevokeOne(s.id)} disabled={revoking === s.id} style={{ padding: '3px 10px', background: 'rgba(217,79,79,.08)', border: '1px solid rgba(217,79,79,.25)', color: 'var(--red)', fontSize: 9, fontWeight: 700, letterSpacing: '.05em', cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 3, textTransform: 'uppercase' }}>
                      Revoke
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {confirm && <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)}/>}
    </div>
  );
}

// ── Active Session Card (mobile) ──────────────────────────────
function SessionCard({ s, i, onHistory, onLogout, revoking }) {
  const ua  = parseUA(s.userAgent);
  const loc = s.city && s.country ? `${s.city}, ${s.country}` : s.country || '—';
  return (
    <div style={{ background: 'var(--dark2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* User row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 30, height: 30, borderRadius: 6, background: AVATAR_COLORS[i % AVATAR_COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: 'var(--black)', flexShrink: 0 }}>
          {initials(s.user.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.user.name}</div>
          <div style={{ fontSize: 10, color: 'var(--gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.user.email}</div>
        </div>
      </div>
      {/* Meta row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px', fontSize: 11, color: 'var(--gray)' }}>
        <span>📍 {loc}</span>
        <span>{ua.device}</span>
        <span style={{ fontFamily: 'monospace', fontSize: 10 }}>{s.ipAddress || '—'}</span>
        <span>{ua.browser} · {ua.os}</span>
        <span style={{ gridColumn: '1 / -1', color: 'var(--gray)', fontSize: 10 }}>Logged in {fmtTime(s.createdAt)}</span>
      </div>
      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => onHistory(s)} style={{ flex: 1, padding: '7px 0', background: 'rgba(212,168,67,.08)', border: '1px solid rgba(212,168,67,.2)', color: 'var(--gold)', fontSize: 10, fontWeight: 700, letterSpacing: '.04em', cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 4, textTransform: 'uppercase' }}>History</button>
        <button onClick={() => onLogout(s)} disabled={revoking === s.user.id} style={{ flex: 1, padding: '7px 0', background: 'rgba(217,79,79,.08)', border: '1px solid rgba(217,79,79,.25)', color: 'var(--red)', fontSize: 10, fontWeight: 700, letterSpacing: '.04em', cursor: revoking === s.user.id ? 'not-allowed' : 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 4, textTransform: 'uppercase', opacity: revoking === s.user.id ? .5 : 1 }}>⊘ Logout</button>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function AdminSessions() {
  const [tab,         setTab]         = useState('active');
  const [active,      setActive]      = useState([]);
  const [suspicious,  setSuspicious]  = useState([]);
  const [loadingA,    setLoadingA]    = useState(true);
  const [loadingS,    setLoadingS]    = useState(true);
  const [historyUser, setHistoryUser] = useState(null);
  const [confirm,     setConfirm]     = useState(null);
  const [revoking,    setRevoking]    = useState(null);

  useEffect(() => {
    adminApi.getActiveSessions()
      .then(d => setActive(d.sessions || []))
      .finally(() => setLoadingA(false));
    adminApi.getSuspiciousActivity()
      .then(d => setSuspicious(d.flags || []))
      .finally(() => setLoadingS(false));
  }, []);

  const handleForceLogout = (session) => {
    setConfirm({
      message: `Force-logout ${session.user.name}? All their active sessions will be revoked.`,
      onConfirm: async () => {
        setConfirm(null);
        setRevoking(session.user.id);
        try {
          await adminApi.revokeUserSessions(session.user.id);
          setActive(s => s.filter(t => t.user.id !== session.user.id));
        } finally { setRevoking(null); }
      },
    });
  };

  const severityColor = s => s === 'HIGH' ? 'var(--red)' : 'var(--warning)';
  const severityBg    = s => s === 'HIGH' ? 'rgba(217,79,79,.1)' : 'rgba(251,191,36,.1)';
  const flagIcon      = type => ({ MULTIPLE_IPS: '🌐', GEO_ANOMALY: '📍', RAPID_SESSIONS: '⚡' }[type] || '⚠');

  return (
    <>
      <InjectStyle id="sess-mobile-css" css={MOBILE_CSS}/>
      <div style={{ padding: '14px 14px 60px', animation: 'pageIn .3s ease', display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Header stats */}
        <div className="sess-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {[
            { l: 'Active Now',    v: active.length,     c: 'var(--green)' },
            { l: 'Suspicious',    v: suspicious.length, c: suspicious.length > 0 ? 'var(--red)' : 'var(--green)' },
            { l: 'High Severity', v: suspicious.filter(f => f.severity === 'HIGH').length, c: 'var(--red)' },
          ].map(({ l, v, c }) => (
            <div key={l} style={{ background: 'var(--dark2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 14px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${c}, transparent 70%)` }}/>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--gray)', marginBottom: 7 }}>{l}</div>
              <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, color: c, lineHeight: 1 }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', background: 'var(--dark2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
          {[['active', '● Active Sessions'], ['suspicious', '⚠ Suspicious Activity']].map(([v, l]) => (
            <button
              key={v} onClick={() => setTab(v)}
              style={{ flex: 1, padding: '11px 8px', background: tab === v ? 'rgba(212,168,67,.08)' : 'transparent', border: 'none', borderRight: '1px solid rgba(255,255,255,.06)', color: tab === v ? 'var(--gold)' : 'rgba(247,243,234,.45)', fontSize: 12, fontWeight: tab === v ? 700 : 500, cursor: 'pointer', fontFamily: "'Inter',sans-serif", letterSpacing: '.02em', transition: 'all .15s' }}
            >
              {l}
              {v === 'suspicious' && suspicious.length > 0 && (
                <span style={{ marginLeft: 7, background: 'var(--red)', color: 'var(--white)', fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 10 }}>{suspicious.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Active Sessions Tab ── */}
        {tab === 'active' && (
          <>
            {loadingA ? <Spinner/> : !active.length ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray)', fontSize: 13, background: 'var(--dark2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>No active sessions right now.</div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="panel sess-table-only" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          {['User', 'Location', 'Device', 'Browser', 'IP Address', 'Logged In', 'Actions'].map(h => (
                            <th key={h} style={{ padding: '11px 14px', fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--gray)', fontWeight: 600, background: 'var(--dark3)', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {active.map((s, i) => {
                          const ua  = parseUA(s.userAgent);
                          const loc = s.city && s.country ? `${s.city}, ${s.country}` : s.country || '—';
                          return (
                            <tr key={s.id} style={{ borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                              <td style={{ padding: '11px 14px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                  <div style={{ width: 28, height: 28, borderRadius: 6, background: AVATAR_COLORS[i % AVATAR_COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: 'var(--black)', flexShrink: 0 }}>{initials(s.user.name)}</div>
                                  <div>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--white)' }}>{s.user.name}</div>
                                    <div style={{ fontSize: 10, color: 'var(--gray)' }}>{s.user.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--off-white)' }}>{loc}</td>
                              <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--off-white)' }}>{ua.device}</td>
                              <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--gray)' }}>{ua.browser} · {ua.os}</td>
                              <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 11, color: 'var(--gray)' }}>{s.ipAddress || '—'}</td>
                              <td style={{ padding: '11px 14px', fontSize: 11, color: 'var(--gray)', whiteSpace: 'nowrap' }}>{fmtTime(s.createdAt)}</td>
                              <td style={{ padding: '11px 14px' }}>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <button onClick={() => setHistoryUser({ id: s.user.id, name: s.user.name })} style={{ padding: '4px 9px', background: 'rgba(212,168,67,.08)', border: '1px solid rgba(212,168,67,.2)', color: 'var(--gold)', fontSize: 9, fontWeight: 700, letterSpacing: '.04em', cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 4, textTransform: 'uppercase' }}>History</button>
                                  <button onClick={() => handleForceLogout(s)} disabled={revoking === s.user.id} style={{ padding: '4px 9px', background: 'rgba(217,79,79,.08)', border: '1px solid rgba(217,79,79,.25)', color: 'var(--red)', fontSize: 9, fontWeight: 700, letterSpacing: '.04em', cursor: revoking === s.user.id ? 'not-allowed' : 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 4, textTransform: 'uppercase', opacity: revoking === s.user.id ? .5 : 1 }}>⊘ Logout</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile cards */}
                <div className="sess-card-only" style={{ display: 'none', flexDirection: 'column', gap: 10 }}>
                  {active.map((s, i) => (
                    <SessionCard
                      key={s.id}
                      s={s} i={i}
                      revoking={revoking}
                      onHistory={s => setHistoryUser({ id: s.user.id, name: s.user.name })}
                      onLogout={handleForceLogout}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* ── Suspicious Activity Tab ── */}
        {tab === 'suspicious' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {loadingS ? <Spinner/> : !suspicious.length ? (
              <div style={{ padding: 40, textAlign: 'center', background: 'var(--dark2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>✅</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>No suspicious activity detected</div>
                <div style={{ fontSize: 12, color: 'var(--gray)' }}>All user sessions look normal in the last 7 days.</div>
              </div>
            ) : suspicious.map((flag, i) => (
              <div key={i} style={{ background: 'var(--dark2)', border: `1px solid ${severityColor(flag.severity)}30`, borderLeft: `3px solid ${severityColor(flag.severity)}`, borderRadius: 'var(--radius)', padding: '14px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 200 }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{flagIcon(flag.type)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>{flag.user.name}</span>
                        <span style={{ fontSize: 11, color: 'var(--gray)' }}>{flag.user.email}</span>
                        <Tag text={flag.severity} color={severityColor(flag.severity)} bg={severityBg(flag.severity)}/>
                        <Tag text={flag.type.replace('_', ' ')} color="var(--gray)" bg="rgba(148,163,184,.08)"/>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--off-white)' }}>{flag.detail}</div>
                      {flag.ips && (
                        <div style={{ marginTop: 5, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          {flag.ips.map(ip => (
                            <span key={ip} style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--gray)', background: 'rgba(255,255,255,.05)', padding: '1px 7px', borderRadius: 3 }}>{ip}</span>
                          ))}
                        </div>
                      )}
                      {flag.countries && (
                        <div style={{ marginTop: 5, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          {flag.countries.map(c => (
                            <span key={c} style={{ fontSize: 11, color: 'var(--off-white)', background: 'rgba(255,255,255,.06)', padding: '2px 8px', borderRadius: 3 }}>📍 {c}</span>
                          ))}
                        </div>
                      )}
                      <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 6 }}>{fmtTime(flag.timestamp)}</div>
                    </div>
                  </div>
                  <div className="sess-flag-actions" style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => setHistoryUser({ id: flag.userId, name: flag.user.name })} style={{ padding: '7px 12px', background: 'rgba(212,168,67,.08)', border: '1px solid rgba(212,168,67,.2)', color: 'var(--gold)', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 4, letterSpacing: '.04em', textTransform: 'uppercase' }}>Sessions</button>
                    <button onClick={() => {
                      setConfirm({
                        message: `Force-logout ${flag.user.name}? All sessions will be revoked immediately.`,
                        onConfirm: async () => {
                          setConfirm(null);
                          await adminApi.revokeUserSessions(flag.userId);
                          setActive(s => s.filter(t => t.user.id !== flag.userId));
                          setSuspicious(f => f.filter((_, fi) => fi !== i));
                        },
                      });
                    }} style={{ padding: '7px 12px', background: 'rgba(217,79,79,.1)', border: '1px solid rgba(217,79,79,.3)', color: 'var(--red)', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 4, letterSpacing: '.04em', textTransform: 'uppercase' }}>⊘ Logout</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {historyUser && (
          <SessionHistoryModal
            userId={historyUser.id}
            userName={historyUser.name}
            onClose={() => setHistoryUser(null)}
          />
        )}
        {confirm && <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)}/>}
      </div>
    </>
  );
}